import React from "react";
import { Box, Card, Typography } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Article } from "../types";
import { absoluteTime, relativeTime } from "../utils/date";
import { ACCENT, surface, text } from "../theme";
import { ArticleMedia } from "./ArticleMedia";
import { SourceAvatar } from "./SourceAvatar";

interface Props {
  article: Article;
  sourceImage?: string;
}

export const ArticleCard = React.memo(function ArticleCard({
  article,
  sourceImage,
}: Props) {
  return (
    <Card
      component="article"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "border-color .2s ease, transform .2s ease",
        "&:hover": {
          borderColor: surface.borderStrong,
          "& .article-image": { transform: "scale(1.04)" },
          "& .article-title": { color: ACCENT },
          "& .article-open": { opacity: 1 },
        },
      }}
    >
      <ArticleMedia article={article} />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 2,
        }}
      >
        <Box
          component="a"
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: "none",
            color: "inherit",
            display: "block",
          }}
        >
          <Typography
            component="h3"
            className="article-title"
            variant="h3"
            sx={{
              color: text.primary,
              transition: "color .15s ease",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.title}
          </Typography>
        </Box>

        {article.excerpt && (
          <Typography
            variant="body2"
            sx={{
              color: text.secondary,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.excerpt}
          </Typography>
        )}

        <Box
          sx={{
            mt: "auto",
            pt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
          }}
        >
          <SourceAvatar title={article.feedTitle} image={sourceImage} />
          <Typography
            variant="caption"
            sx={{
              color: text.tertiary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              minWidth: 0,
            }}
          >
            {article.feedTitle}
          </Typography>
          <Typography
            component="time"
            dateTime={article.pubDate}
            title={absoluteTime(article.pubDate)}
            variant="caption"
            sx={{ color: text.tertiary, flexShrink: 0 }}
          >
            {relativeTime(article.pubDate)}
          </Typography>
          <OpenInNewRoundedIcon
            className="article-open"
            aria-hidden
            sx={{
              fontSize: 14,
              color: text.tertiary,
              opacity: 0,
              transition: "opacity .15s ease",
            }}
          />
        </Box>
      </Box>
    </Card>
  );
});
